import { ImagePlusIcon, LockIcon, PaperclipIcon, SendIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { fmtDateTime, fmtRelative } from '@/lib/format';
import type { SupportMessageDto, SupportRequestDto } from '@/lib/types';
import { cn, initials } from '@/lib/utils';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_FILES = 5;
const MAX_MB = 5;

/**
 * The conversation on a help request: the original message first, then every reply in order, with
 * image attachments inline. Staff replies sit on the right; internal notes are marked and shown only
 * to staff. The composer takes text plus up to five images (picked, pasted or dropped).
 */
export function SupportThread({
  request,
  messages,
  loading,
  viewer,
  onReply,
  busy,
}: {
  request: SupportRequestDto;
  messages: SupportMessageDto[] | undefined;
  loading: boolean;
  /** Who is looking: staff see and can write internal notes. */
  viewer: 'staff' | 'requester';
  onReply: (input: { body: string; internal: boolean; files: File[] }) => Promise<unknown>;
  busy: boolean;
}) {
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages?.length]);

  const addFiles = (list: FileList | File[] | null | undefined) => {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      if (!IMAGE_TYPES.includes(f.type)) {
        toast.error(`${f.name}: only PNG, JPEG, GIF or WebP images`);
        continue;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_MB} MB`);
        continue;
      }
      if (next.length >= MAX_FILES) {
        toast.error(`Up to ${MAX_FILES} images per reply`);
        break;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const send = async () => {
    const text = body.trim();
    if (!text && files.length === 0) return;
    try {
      await onReply({ body: text || (files.length ? '(see attached)' : ''), internal: viewer === 'staff' && internal, files });
      setBody('');
      setFiles([]);
      setInternal(false);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not send your reply'));
    }
  };

  const entries: Array<{ key: string; authorKind: SupportMessageDto['authorKind']; authorName: string; body: string; internal: boolean; attachments: SupportMessageDto['attachments']; createdAt: string }> = [
    { key: 'original', authorKind: 'requester', authorName: request.requester.name, body: request.message, internal: false, attachments: [], createdAt: request.createdAt },
    // Org admins who filed a request see their own thread as requesters; keep staff-only notes out of that view.
    ...(messages ?? []).filter((m) => viewer === 'staff' || !m.internal).map((m) => ({ key: m.id, authorKind: m.authorKind, authorName: m.authorName, body: m.body, internal: m.internal, attachments: m.attachments, createdAt: m.createdAt })),
  ];

  return (
    <div className="flex min-h-0 flex-col gap-6">
      <ol className="grid gap-4" aria-label="Conversation">
        {entries.map((e) => {
          const staff = e.authorKind !== 'requester';
          const mine = viewer === 'staff' ? staff : !staff;
          return (
            <li key={e.key} className={cn('flex gap-3', mine && 'flex-row-reverse')}>
              <span className={cn('grid size-8 shrink-0 place-items-center text-[10px] font-semibold', staff ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')} aria-hidden>
                {initials(e.authorName)}
              </span>
              <div className={cn('grid max-w-[720px] min-w-0 flex-1 gap-1', mine && 'justify-items-end')}>
                <div className={cn('text-muted-foreground flex items-baseline gap-2 text-xs', mine && 'flex-row-reverse')}>
                  <span className="text-foreground font-medium">{e.authorName}</span>
                  <span>{e.authorKind === 'platform_admin' ? 'Crewline support' : e.authorKind === 'org_admin' ? 'Organization admin' : 'Requester'}</span>
                  <span title={fmtDateTime(e.createdAt)}>· {fmtRelative(e.createdAt)}</span>
                </div>
                <div className={cn('border p-4 text-sm leading-relaxed whitespace-pre-wrap', e.internal ? 'border-warning/50 bg-warning/10' : mine ? 'bg-accent/50' : 'bg-card')}>
                  {e.internal && (
                    <div className="text-warning mb-2 flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase">
                      <LockIcon className="size-3" aria-hidden /> Internal note · not visible to the requester
                    </div>
                  )}
                  {e.body}
                  {e.attachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {e.attachments.map((a) => (
                        <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="group bg-muted relative block h-28 w-40 overflow-hidden border" title={`${a.name} · ${Math.round(a.sizeBytes / 1024)} KB`}>
                          <img src={a.url} alt={a.name} className="size-full object-cover transition-transform group-hover:scale-105" />
                          <span className="bg-background/90 absolute inset-x-0 bottom-0 truncate px-2 py-1 text-[11px]">{a.name}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
        {loading && !messages && (
          <li className="flex gap-3">
            <Skeleton className="size-8" />
            <Skeleton className="h-16 flex-1" />
          </li>
        )}
        <div ref={endRef} />
      </ol>

      {/* Composer */}
      <div
        className={cn('bg-card border transition-colors', dragging && 'border-primary bg-accent/30')}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onPaste={(e) => {
            const imgs = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
            if (imgs.length) {
              e.preventDefault();
              addFiles(imgs);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
          rows={4}
          placeholder={viewer === 'staff' ? 'Write a reply to the requester, or an internal note for your team. Paste or drop screenshots.' : 'Add details, or answer a question from the team. Paste or drop screenshots.'}
          className="min-h-[104px] rounded-none border-0 bg-transparent px-4 py-3 shadow-none focus-visible:ring-0"
          aria-label="Reply"
          maxLength={4000}
        />
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t px-4 py-3">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="group relative h-20 w-28 overflow-hidden border">
                <img src={previews[i]} alt={f.name} className="size-full object-cover" />
                <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="bg-background/90 hover:bg-background absolute top-1 right-1 grid size-6 place-items-center border" aria-label={`Remove ${f.name}`}>
                  <XIcon className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t px-3 py-2">
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} multiple className="sr-only" onChange={(e) => addFiles(e.target.files)} />
            <Button type="button" variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={files.length >= MAX_FILES}>
              <ImagePlusIcon /> Attach images
            </Button>
            {viewer === 'staff' && (
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={internal} onCheckedChange={setInternal} aria-label="Internal note" />
                <span className={cn('flex items-center gap-1', internal && 'text-warning font-medium')}>
                  <LockIcon className="size-3.5" aria-hidden /> Internal note
                </span>
              </label>
            )}
            <span className="text-muted-foreground hidden items-center gap-1 text-xs sm:flex">
              <PaperclipIcon className="size-3" aria-hidden /> Up to {MAX_FILES} images, {MAX_MB} MB each
            </span>
          </div>
          <Button size="sm" onClick={() => void send()} disabled={busy || (!body.trim() && files.length === 0)}>
            <SendIcon /> {busy ? 'Sending…' : internal ? 'Add note' : 'Send reply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
